import type { Interval, Nullable } from '@library/types'
import type { Path, Selection } from 'd3'
import { path as createPath } from 'd3'

import { GRADIENT_OFFSET, START_GRADIENT_OFFSET } from '../constants'

const CORNER_RADIUS = 1
const MIN_PICK_HEIGHT = 2
const START_HISTOGRAM_Y_OFFSET = START_GRADIENT_OFFSET
const START_HISTOGRAM_X_OFFSET = 0

const getMinimalCornerRadius = (firstPickX: number, secondPickX: number) => {
  const dx = Math.abs(firstPickX - secondPickX)
  return dx < CORNER_RADIUS ? dx / 2 : CORNER_RADIUS
}

const drawTopOfPick = (path: Path, prevPickX: number, pickX: number, y: number) => {
  const cornerRadius = getMinimalCornerRadius(prevPickX, pickX)

  if (prevPickX < pickX) {
    path.lineTo(pickX - cornerRadius, y)
    path.quadraticCurveTo(pickX, y, pickX, y + cornerRadius)
  } else {
    path.lineTo(pickX + cornerRadius, y)
    path.quadraticCurveTo(pickX, y, pickX, y + cornerRadius)
  }
}

const drawBottomOfPick = (path: Path, pickX: number, nextPickX: number, y: number) => {
  const cornerRadius = getMinimalCornerRadius(nextPickX, pickX)
  path.lineTo(pickX, y - cornerRadius)

  if (nextPickX < pickX) {
    path.quadraticCurveTo(pickX, y, pickX - cornerRadius, y)
  } else {
    path.quadraticCurveTo(pickX, y, pickX + cornerRadius, y)
  }
}

const drawPick = (
  path: Path,
  prevPickX: number,
  pickX: number,
  nextPickX: number,
  y: number,
  step: number,
) => {
  drawTopOfPick(path, prevPickX, pickX, y)

  const nextY = y + step

  drawBottomOfPick(path, pickX, nextPickX, nextY)
}

const startPath = (path: Path, x: number, y: number) => {
  path.moveTo(x, y + CORNER_RADIUS)
  path.quadraticCurveTo(x, y, x + CORNER_RADIUS, y)
}

const closePath = (path: Path, x: number, y: number) => {
  path.lineTo(x + CORNER_RADIUS, y)
  path.quadraticCurveTo(x, y, x, y - CORNER_RADIUS)
  path.closePath()
}

const getPickCoord = (count: number, maxCount: number, maxPickHeight: number) =>
  MIN_PICK_HEIGHT + (count / maxCount) * maxPickHeight

export const createHistogramPath = (
  container: Selection<Nullable<SVGGElement>, unknown, null, undefined>,
  id: string,
  width: number,
  height: number,
  intervals: Interval[],
) => {
  const maxCount = intervals.reduce(
    (prev, current) => Math.max(prev, current.count),
    intervals[0].count,
  )
  const histogramWidth = width - MIN_PICK_HEIGHT
  const histogramHeight = height - GRADIENT_OFFSET
  const step = histogramHeight / intervals.length
  let y = START_HISTOGRAM_Y_OFFSET
  let x = START_HISTOGRAM_X_OFFSET
  let prevX = x + CORNER_RADIUS

  const path = createPath()
  startPath(path, x, y)
  for (let index = intervals.length - 1; index >= 0; index--) {
    x = getPickCoord(intervals[index].count, maxCount, histogramWidth)
    const nextX =
      index - 1 >= 0
        ? getPickCoord(intervals[index - 1].count, maxCount, histogramWidth)
        : START_HISTOGRAM_X_OFFSET

    drawPick(path, prevX, x, nextX, y, step)

    y += step
    prevX = x
  }

  closePath(path, START_HISTOGRAM_X_OFFSET, y)

  return container.append('path').attr('fill', `url(#${id})`).attr('d', path.toString())
}
